import { useEffect, useRef } from "react";
import * as THREE from "three";

export const HeroCube = () => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
    camera.position.set(0, 0, 9);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    el.appendChild(renderer.domElement);

    const gold = new THREE.Color("#00f2fe");
    const light = new THREE.Color("#7df9ff");
    const group = new THREE.Group();
    scene.add(group);

    const outer = new THREE.LineSegments(
      new THREE.EdgesGeometry(new THREE.BoxGeometry(3.2, 3.2, 3.2)),
      new THREE.LineBasicMaterial({ color: gold, transparent: true, opacity: 0.9 })
    );
    group.add(outer);

    const inner = new THREE.LineSegments(
      new THREE.EdgesGeometry(new THREE.BoxGeometry(1.7, 1.7, 1.7)),
      new THREE.LineBasicMaterial({ color: light, transparent: true, opacity: 0.6 })
    );
    group.add(inner);

    const corners = [];
    const pts = [];
    for (let x = -1; x <= 1; x += 2) for (let y = -1; y <= 1; y += 2) for (let z = -1; z <= 1; z += 2) {
      pts.push(new THREE.Vector3(x * 1.6, y * 1.6, z * 1.6), new THREE.Vector3(x * 0.85, y * 0.85, z * 0.85));
      corners.push(new THREE.Vector3(x * 1.6, y * 1.6, z * 1.6));
    }
    const links = new THREE.LineSegments(
      new THREE.BufferGeometry().setFromPoints(pts),
      new THREE.LineBasicMaterial({ color: gold, transparent: true, opacity: 0.35 })
    );
    group.add(links);

    const nodeGeo = new THREE.SphereGeometry(0.07, 16, 16);
    const nodeMat = new THREE.MeshBasicMaterial({ color: light });
    corners.forEach((c) => {
      const m = new THREE.Mesh(nodeGeo, nodeMat);
      m.position.copy(c);
      group.add(m);
    });

    const core = new THREE.Mesh(
      new THREE.OctahedronGeometry(0.55, 0),
      new THREE.MeshStandardMaterial({ color: gold, metalness: 1, roughness: 0.25, emissive: new THREE.Color("#063a52"), emissiveIntensity: 0.6 })
    );
    group.add(core);

    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(2.8, 0.012, 8, 160),
      new THREE.MeshBasicMaterial({ color: gold, transparent: true, opacity: 0.5 })
    );
    ring.rotation.x = Math.PI / 2.4;
    group.add(ring);
    const ring2 = ring.clone();
    ring2.rotation.set(Math.PI / 1.7, 0.6, 0);
    ring2.material = ring.material.clone();
    ring2.material.opacity = 0.25;
    group.add(ring2);

    const pCount = 220;
    const pPos = new Float32Array(pCount * 3);
    for (let i = 0; i < pCount; i++) {
      const r = 3.6 + Math.random() * 2.4;
      const th = Math.random() * Math.PI * 2;
      const ph = Math.acos(2 * Math.random() - 1);
      pPos[i * 3] = r * Math.sin(ph) * Math.cos(th);
      pPos[i * 3 + 1] = r * Math.sin(ph) * Math.sin(th);
      pPos[i * 3 + 2] = r * Math.cos(ph);
    }
    const particles = new THREE.Points(
      new THREE.BufferGeometry().setAttribute("position", new THREE.BufferAttribute(pPos, 3)),
      new THREE.PointsMaterial({ color: gold, size: 0.035, transparent: true, opacity: 0.7, sizeAttenuation: true })
    );
    scene.add(particles);

    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const key = new THREE.PointLight(0x7df9ff, 30, 30);
    key.position.set(4, 4, 5);
    scene.add(key);
    const rim = new THREE.PointLight(0x4facfe, 18, 30);
    rim.position.set(-5, -3, 3);
    scene.add(rim);

    const target = { x: 0, y: 0 };
    const onMove = (e) => {
      target.x = (e.clientX / window.innerWidth - 0.5) * 2;
      target.y = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener("pointermove", onMove, { passive: true });

    const resize = () => {
      const w = el.clientWidth || 1;
      const h = el.clientHeight || 1;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(el);

    let raf;
    const clock = new THREE.Clock();
    const tick = () => {
      const t = clock.getElapsedTime();
      group.rotation.y = t * 0.25 + target.x * 0.5;
      group.rotation.x += (target.y * 0.35 - group.rotation.x) * 0.04;
      group.rotation.z = Math.sin(t * 0.3) * 0.08;
      group.position.y = Math.sin(t * 0.8) * 0.15;
      inner.rotation.x = -t * 0.4;
      inner.rotation.y = t * 0.5;
      core.rotation.y = t * 0.9;
      core.rotation.x = t * 0.4;
      ring.rotation.z = t * 0.2;
      ring2.rotation.z = -t * 0.15;
      particles.rotation.y = t * 0.03;
      camera.position.x += (target.x * 0.8 - camera.position.x) * 0.03;
      camera.position.y += (-target.y * 0.5 - camera.position.y) * 0.03;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("pointermove", onMove);
      renderer.dispose();
      scene.traverse((o) => {
        if (o.geometry) o.geometry.dispose();
        if (o.material) (Array.isArray(o.material) ? o.material : [o.material]).forEach((m) => m.dispose());
      });
      el.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={ref} className="w-full h-full [&>canvas]:w-full [&>canvas]:h-full" data-testid="hero-3d-cube" />;
};
